/**
 * Ciclo de vida da cotação (RN-040 a RN-043).
 *
 * Duas ideias sustentam este módulo:
 *
 * 1. Transição inválida é ERRO, não correção silenciosa. Uma cotação que pula
 *    de rascunho para respondida não passou por aprovação nem por envio, e o
 *    histórico ficaria mentindo sobre o que aconteceu.
 *
 * 2. Vencimento é ESTADO DERIVADO, não gravado. Uma cotação está vencida
 *    quando a validade passou — ponto. Guardar isso num campo criaria duas
 *    fontes de verdade e obrigaria a manter um processo rodando de madrugada
 *    só para sincronizá-las. É a observação que dispensou o servidor.
 */

export type StatusCotacao =
  | 'RASCUNHO'
  | 'AGUARDANDO_APROVACAO'
  | 'ENVIADA'
  | 'RESPONDIDA'
  | 'PARCIALMENTE_RESPONDIDA'
  | 'NECESSITA_REVISAO'
  | 'EXPIRADA'
  | 'CANCELADA';

export const ROTULO_STATUS: Record<StatusCotacao, string> = {
  RASCUNHO: 'Rascunho',
  AGUARDANDO_APROVACAO: 'Aguardando aprovação',
  ENVIADA: 'Enviada',
  RESPONDIDA: 'Respondida',
  PARCIALMENTE_RESPONDIDA: 'Parcialmente respondida',
  NECESSITA_REVISAO: 'Necessita revisão',
  EXPIRADA: 'Expirada',
  CANCELADA: 'Cancelada',
};

/** Transições permitidas. EXPIRADA não aparece: é derivada, ninguém a grava. */
const TRANSICOES: Record<StatusCotacao, StatusCotacao[]> = {
  RASCUNHO: ['AGUARDANDO_APROVACAO', 'ENVIADA', 'CANCELADA'],
  AGUARDANDO_APROVACAO: ['ENVIADA', 'RASCUNHO', 'CANCELADA'],
  ENVIADA: ['RESPONDIDA', 'PARCIALMENTE_RESPONDIDA', 'NECESSITA_REVISAO', 'CANCELADA'],
  PARCIALMENTE_RESPONDIDA: ['RESPONDIDA', 'NECESSITA_REVISAO', 'CANCELADA'],
  NECESSITA_REVISAO: ['RESPONDIDA', 'PARCIALMENTE_RESPONDIDA', 'CANCELADA'],
  RESPONDIDA: ['NECESSITA_REVISAO'],
  EXPIRADA: [],
  CANCELADA: [],
};

export const transicoesDe = (de: StatusCotacao): StatusCotacao[] => TRANSICOES[de];

export const podeTransitar = (de: StatusCotacao, para: StatusCotacao): boolean =>
  TRANSICOES[de].includes(para);

export function exigirTransicao(de: StatusCotacao, para: StatusCotacao): void {
  if (!podeTransitar(de, para)) {
    throw new Error(
      `Transição inválida: ${ROTULO_STATUS[de]} não pode virar ${ROTULO_STATUS[para]}.`,
    );
  }
}

/**
 * Status como ele deve APARECER, considerando a validade (RN-041).
 *
 * Datas em ISO curto (AAAA-MM-DD) comparam corretamente como string, o que
 * evita fuso horário entrar na conta — uma cotação vence no dia, não no
 * instante.
 */
export function statusEfetivo(
  status: StatusCotacao,
  validade: string | null,
  hoje: string,
): StatusCotacao {
  if (status === 'CANCELADA' || status === 'RASCUNHO' || status === 'AGUARDANDO_APROVACAO') return status;
  if (validade && validade < hoje) return 'EXPIRADA';
  return status;
}

export const estaVencida = (validade: string | null, hoje: string): boolean =>
  validade !== null && validade < hoje;

/** Status que a cotação deve assumir conforme as respostas chegam (RN-043). */
export function statusPorRespostas(
  totalItens: number,
  itensRespondidos: number,
  algumRequerRevisao: boolean,
): StatusCotacao | null {
  if (totalItens === 0 || itensRespondidos === 0) return null;
  if (algumRequerRevisao) return 'NECESSITA_REVISAO';
  return itensRespondidos >= totalItens ? 'RESPONDIDA' : 'PARCIALMENTE_RESPONDIDA';
}

/** Uma cotação vencida ou cancelada não alimenta análise nova (RN-042). */
export const podeUsarEmAnalise = (status: StatusCotacao): boolean =>
  status === 'RESPONDIDA' || status === 'PARCIALMENTE_RESPONDIDA';

/** Referência sequencial legível: COT-2026-0042. */
export const referenciaCotacao = (ano: number, sequencial: number): string =>
  `COT-${ano}-${String(sequencial).padStart(4, '0')}`;
